// Company (U12) — legacy utilities/CompanyFormComponent.vue. Customer / supplier
// portal users edit their own party record via `GET/POST user/company`.

import { FormEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { Building } from 'iconsax-reactjs';

import { apiErrorMessage, apiValidationErrors } from '../../api/client';
import { useAuth } from '../../components/AuthGate';
import { ErrorBlock, LoadingBlock } from '../../components/StateBlock';
import { t } from '../../i18n';
import { WorkspaceFieldGrid, WorkspaceSection } from '../../workspace/WorkspaceSection';
import { ShellUser, fetchCompany, fetchCountries, fetchStates, saveCompany } from './api';

const TEXT_FIELDS = ['name', 'company', 'phone', 'email', 'house_no', 'street_no', 'address', 'city', 'postal_code'] as const;

export function CompanyPage() {
  const { user } = useAuth();
  const shellUser = user as ShellUser | null;
  const isPortal = Boolean(shellUser?.customer_id || shellUser?.supplier_id);
  const [form, setForm] = useState<Record<string, unknown> | null>(null);
  const [countries, setCountries] = useState<Array<{ value: string; label: string }>>([]);
  const [states, setStates] = useState<Array<{ value: string; label: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!isPortal) return;
    fetchCompany()
      .then((data) => setForm(data))
      .catch((err) => setError(apiErrorMessage(err, t('common.unableToLoad'))));
    fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, [isPortal]);

  const country = String(form?.country ?? '');
  useEffect(() => {
    if (!country) return setStates([]);
    fetchStates(country).then(setStates).catch(() => setStates([]));
  }, [country]);

  if (!isPortal) return <Alert severity="info">{t('shell.company.notPortal')}</Alert>;
  if (error) return <ErrorBlock message={error} />;
  if (!form) return <LoadingBlock />;

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...(prev ?? {}), [key]: value }));
  const fieldError = (key: string) => errors[key]?.[0];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const payload: Record<string, unknown> = {};
      for (const key of [...TEXT_FIELDS, 'state', 'country', 'extra_attributes']) if (form[key] !== undefined) payload[key] = form[key] ?? null;
      const result = await saveCompany(payload);
      setToast(result.success ? { msg: t('shell.company.saved'), sev: 'success' } : { msg: t('common.saveFailed'), sev: 'error' });
    } catch (err) {
      setErrors(apiValidationErrors(err));
      setToast({ msg: apiErrorMessage(err, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const stateValue = String(form.state ?? '');
  const stateOptions = stateValue && !states.some((s) => s.value === stateValue) ? [{ value: stateValue, label: stateValue }, ...states] : states;

  return (
    <Stack spacing={2.5} component="form" onSubmit={submit} sx={{ maxWidth: 960 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Building size={28} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('nav.company')}
        </Typography>
      </Stack>
      <WorkspaceSection title={t('shell.company.details')} description={t('shell.company.detailsDesc')}>
        <WorkspaceFieldGrid>
          {TEXT_FIELDS.map((key) => (
            <TextField
              key={key}
              required={key === 'name'}
              type={key === 'email' ? 'email' : 'text'}
              label={t(`shell.company.${key}`)}
              value={String(form[key] ?? '')}
              onChange={(e) => setField(key, e.target.value)}
              error={Boolean(fieldError(key))}
              helperText={fieldError(key)}
            />
          ))}
          <TextField
            select
            label={t('shell.company.country')}
            value={country}
            onChange={(e) => {
              setField('country', e.target.value);
              setField('state', '');
            }}
            error={Boolean(fieldError('country'))}
            helperText={fieldError('country')}
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {countries.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('shell.company.state')}
            value={stateValue}
            disabled={!country}
            onChange={(e) => setField('state', e.target.value)}
            error={Boolean(fieldError('state'))}
            helperText={fieldError('state')}
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {stateOptions.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </WorkspaceFieldGrid>
      </WorkspaceSection>
      <Box>
        <Button type="submit" variant="contained" disabled={saving || !form.name}>
          {saving ? t('action.saving') : t('action.save')}
        </Button>
      </Box>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
